import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { KioskDistributionModePickerPageConfig } from '@front-platform/content-builder';
import { kioskDistributionModePickerPageResolveConfig } from '@front-platform/content-builder';
import { Component, HostBinding, inject, OnInit, input } from '@angular/core';
import {
  IContentBuilderFieldColor,
  IContentBuilderFieldImage,
} from '@core/models/content-builder-fields.model';
import { LinkNavigationService } from '@core/services/link-navigation/link-navigation.service';
import { ShopSearchParametersService } from '@core/services/shop-search-parameters/shop-search-parameters.service';
import { CartStoreDispatcher } from '@common/dispatchers/cart.dispatcher';
import { CartStoreSelector } from '@common/selectors/cart.selector';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { CartKeys } from '@config/keys/cart.keys';
import { TimeSlotsUtils } from '@shared/utils/time-slots/time-slots.utils';
import { DoodWantedAtType } from '@store/settings/settings.model';
import { OrderTypeService } from '@core/services/order-type/order-type.service';
import { DateUtils } from '@shared/utils/date/date.utils';
import { DistributionModeKeys } from '@config/keys/shop.keys';
import { DaySlotsUtils } from '@shared/utils/day-slots/day-slots.utils';
import { TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import isTomorrow from 'dayjs/plugin/isTomorrow';
import { SettingsParametersState } from '@store/settings';
import { takeUntil, tap } from 'rxjs/operators';
import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { ISimpleItem } from '@core/models/simple-item.model';
import { ParametersUtils } from '@shared/utils/parameters/parameters.utils';
import { IDistributionMode } from '@core/models/shop.model';
import { AnalyticsService } from '@core/services/app/analytics.service';
export interface KioskDistributionModePickerPageRuntimeConfig {
  'body'?: any;
  'header'?: unknown[] | undefined;
  'title'?: string;
}

export type KioskDistributionModePickerPageConfigInput = Partial<
  Omit<KioskDistributionModePickerPageConfig, keyof KioskDistributionModePickerPageRuntimeConfig> &
    KioskDistributionModePickerPageRuntimeConfig
>;
export type KioskDistributionModePickerPageResolvedConfig = Omit<
  KioskDistributionModePickerPageConfig,
  keyof KioskDistributionModePickerPageRuntimeConfig
> &
  Partial<KioskDistributionModePickerPageRuntimeConfig>;
export interface KioskDistributionModePickerPageContract extends ConfigComponentContract<
  KioskDistributionModePickerPageConfigInput,
  KioskDistributionModePickerPageResolvedConfig
> {}

@Component({
  selector: 'app-kiosk-distribution-mode-picker-page',
  templateUrl: './kiosk-distribution-mode-picker-page.component.html',
  styleUrls: ['./kiosk-distribution-mode-picker-page.component.scss'],
  standalone: false,
})
export class KioskDistributionModePickerPageComponent
  extends DestroyerBase
  implements OnInit, KioskDistributionModePickerPageContract
{
  readonly config = input<KioskDistributionModePickerPageConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    KioskDistributionModePickerPageConfig,
    KioskDistributionModePickerPageRuntimeConfig
  >(kioskDistributionModePickerPageResolveConfig, () => ({ config: this.config() }));
  get body(): any {
    return this.resolvedConfig()['body'] as unknown as any;
  }
  get header(): unknown[] | undefined {
    return this.resolvedConfig()['header'] as unknown as unknown[] | undefined;
  }
  get title(): string {
    return this.resolvedConfig()['title'] as unknown as string;
  }
  get linkUrl(): string {
    return this.resolvedConfig()['link_url'] as unknown as string;
  }
  get backgroundColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['background_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get backgroundImage(): IContentBuilderFieldImage | undefined {
    return this.resolvedConfig()['background_image'] as unknown as
      IContentBuilderFieldImage | undefined;
  }
  get textColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['text_color'] as unknown as IContentBuilderFieldColor | undefined;
  }
  get accentColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['accent_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }

  get activeCardBackgroundColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()[
      'active_card_background_color'
    ] as unknown as IContentBuilderFieldColor;
  }
  get activeCardTextColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['active_card_text_color'] as unknown as IContentBuilderFieldColor;
  }
  get inactiveCardBackgroundColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()[
      'inactive_card_background_color'
    ] as unknown as IContentBuilderFieldColor;
  }
  get inactiveCardTextColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()[
      'inactive_card_text_color'
    ] as unknown as IContentBuilderFieldColor;
  }

  get primaryButtonBackgroundColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()[
      'primary_button_background_color'
    ] as unknown as IContentBuilderFieldColor;
  }
  get primaryButtonTextColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()[
      'primary_button_text_color'
    ] as unknown as IContentBuilderFieldColor;
  }
  get secondaryButtonBackgroundColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()[
      'secondary_button_background_color'
    ] as unknown as IContentBuilderFieldColor;
  }
  get secondaryButtonTextColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()[
      'secondary_button_text_color'
    ] as unknown as IContentBuilderFieldColor;
  }

  @HostBinding('class') classes = 'h-screen flex text-center';

  availableDistributionModeItems: ISimpleItem[] = [];
  availableDistributionMode: IDistributionMode[] = [];
  availableDatesSlots?: { value: Date; label: string }[];
  availableTimesSlots?: { value: number; label: string; slot: { start: Date; end: Date } }[];
  selectedDateSlot?: Date | null;
  selectedTimeSlot?: Date | null;
  selectedDistributionMode: string | null = null;
  selectedWantedAtType: DoodWantedAtType | null = null;

  private intervalMinuteTimeSlot = 15;
  private readonly linkNavigationService = inject(LinkNavigationService);

  constructor(
    private readonly shopSearchParametersService: ShopSearchParametersService,
    private readonly marketplaceSelector: MarketplaceStoreSelector,
    private readonly orderTypeService: OrderTypeService,
    private readonly translateService: TranslateService,
    private readonly analyticsService: AnalyticsService,
    private readonly cartSelector: CartStoreSelector,
    private readonly cartDispatcher: CartStoreDispatcher,
  ) {
    super();
  }

  ngOnInit(): void {
    dayjs.extend(isTomorrow);
    dayjs.extend(isToday);
    const savedParameters = this.shopSearchParametersService.getParameters();

    this.selectedDistributionMode = savedParameters.distribution_mode;

    this.marketplaceSelector.selectMarketplaceDistributionModes
      .pipe(
        takeUntil(this._destroyerRef),
        tap(distributionModes => {
          this.availableDistributionMode = distributionModes;
          this.availableDistributionModeItems =
            ParametersUtils.mapDistributionModes(distributionModes);
        }),
      )
      .subscribe();

    let hour = 0;
    let minute = 0;

    let now = DateUtils.dayjsInMarketplaceTimezone();
    const preOrderingDelays = this.orderTypeService.getPreOrderDelays(
      this.selectedDistributionMode,
    );

    this.availableTimesSlots = TimeSlotsUtils.generateTimeSlots({
      day: this.selectedDateSlot ?? new Date(),
      start: hour,
      startWithMinute: minute,
      end: 24,
      interval: this.intervalMinuteTimeSlot,
      minDate: now.add(preOrderingDelays[DistributionModeKeys.PreorderingMinDelay], 'seconds'),
      maxDate: now.add(preOrderingDelays[DistributionModeKeys.PreorderingMaxDelay], 'seconds'),
    }).map(slot => {
      return {
        value: slot.id,
        label: `${DateUtils.dayjsInMarketplaceTimezone(slot.start).format(this.translateService.instant('parameters.wantedAt.slots-format'))} - ${DateUtils.dayjsInMarketplaceTimezone(slot.end).format(this.translateService.instant('parameters.wantedAt.slots-format'))}`,
        slot: {
          start: new Date(DateUtils.dayjsInMarketplaceTimezone(slot.start).format()),
          end: new Date(DateUtils.dayjsInMarketplaceTimezone(slot.end).format()),
        },
      };
    });
    this.selectedTimeSlot = this.availableTimesSlots[0].slot.start;

    this.updateDisponibleDateSlots();
    this.onWantedAtTypeChanged(this.selectedWantedAtType ?? DoodWantedAtType.asap);
  }

  onWantedAtTypeChanged(wantedAtType: DoodWantedAtType): void {
    this.selectedWantedAtType = wantedAtType;
    this.shopSearchParametersService.set({
      wanted_at_type: wantedAtType,
    });

    if (wantedAtType === DoodWantedAtType.later) {
      this.updateDisponibleDateSlots();
    } else {
      this.selectedDateSlot = null;
    }
  }

  private updateDisponibleDateSlots(): void {
    const preOrderingDelays = this.orderTypeService.getPreOrderDelays(
      this.selectedDistributionMode ?? undefined,
    );

    const minDate = DateUtils.dayjsInMarketplaceTimezone()
      .add(preOrderingDelays[DistributionModeKeys.PreorderingMinDelay], 'seconds')
      .startOf('day');
    const maxDate = DateUtils.dayjsInMarketplaceTimezone()
      .add(preOrderingDelays[DistributionModeKeys.PreorderingMaxDelay], 'seconds')
      .startOf('day');

    const today = DateUtils.dayjsInMarketplaceTimezone().startOf('day');
    const minDays = minDate.diff(today, 'days');
    const maxDays = maxDate.diff(today, 'days');
    this.availableDatesSlots = DaySlotsUtils.generateDaySlots({
      interval: 1,
      minDays: minDays,
      maxDays: maxDays,
    }).map((date: Date) => {
      return {
        value: date,
        label: DateUtils.dayjsInMarketplaceTimezone(date).isToday()
          ? this.translateService.instant('parameters.wantedAt.today')
          : DateUtils.dayjsInMarketplaceTimezone(date).isTomorrow()
            ? this.translateService.instant('parameters.wantedAt.tomorrow')
            : DateUtils.formatLocalizedDate(
                date,
                this.translateService.instant('date.long-format'),
              ),
      };
    });

    this.selectedDateSlot = this.availableDatesSlots[0].value;
  }

  get hasBackgroundImage(): boolean {
    return !!this.backgroundImage?.url;
  }

  getBackgroundStyles() {
    const url = this.backgroundImage?.url;
    return {
      'background-image': url ? `url(${url})` : null,
      'background-size': url ? 'cover' : null,
      'background-position': url ? 'center' : null,
      'background-repeat': url ? 'no-repeat' : null,
    };
  }

  canValidate() {
    return this.selectedDistributionMode != null;
  }

  selectDistributionMode(distributionModeItem: ISimpleItem) {
    this.selectedDistributionMode = distributionModeItem.value;

    if (this.preorderingAllowed()) {
      this.selectedWantedAtType = DoodWantedAtType.asap;
    } else {
      this.selectedWantedAtType = null;
    }
  }

  navigateto(link: string, set?: boolean) {
    if (set && this.selectedDistributionMode) {
      this.selectedDateSlot?.setHours(this.selectedTimeSlot?.getHours() ?? 0);

      this.shopSearchParametersService.setDistributionModeType(this.selectedDistributionMode, true);
      const toSave: Partial<SettingsParametersState> = {
        is_distribution_mode_defined: true,
        distribution_mode: this.selectedDistributionMode,
      };

      if (this.preorderingAllowed() && this.selectedWantedAtType === DoodWantedAtType.later) {
        toSave.wanted_at = this.selectedDateSlot;
      } else {
        toSave.wanted_at = null;
      }

      this.shopSearchParametersService.set(toSave);
      this.syncExistingCartWithSelectedDistributionMode();
    }
    this.linkNavigationService.navigate(link);
  }

  private syncExistingCartWithSelectedDistributionMode(): void {
    const cart = this.cartSelector.active;
    const selectedMode = this.selectedDistributionMode;
    if (!cart || !selectedMode || cart[CartKeys.Type] === selectedMode) {
      return;
    }

    this.cartDispatcher.updateActive({
      [CartKeys.Type]: selectedMode,
      [CartKeys.Products]: [],
      [CartKeys.IsUserSelectedDistributionMode]: true,
    });
  }

  preorderingAllowed() {
    let distributionMode = this.availableDistributionMode.find(
      distributionMode =>
        distributionMode[DistributionModeKeys.Type] === this.selectedDistributionMode,
    );

    if (!distributionMode || !distributionMode.preordering_allowed) {
      return false;
    }
    return true;
  }

  changeDate() {
    let hour = 0;
    let minute = 0;

    let now = DateUtils.dayjsInMarketplaceTimezone();
    const preOrderingDelays = this.orderTypeService.getPreOrderDelays(
      this.selectedDistributionMode ?? undefined,
    );

    this.availableTimesSlots = TimeSlotsUtils.generateTimeSlots({
      day: this.selectedDateSlot ?? new Date(),
      start: hour,
      startWithMinute: minute,
      end: 24,
      interval: this.intervalMinuteTimeSlot,
      minDate: now.add(preOrderingDelays[DistributionModeKeys.PreorderingMinDelay], 'seconds'),
      maxDate: now.add(preOrderingDelays[DistributionModeKeys.PreorderingMaxDelay], 'seconds'),
    }).map(slot => {
      return {
        value: slot.id,
        label: `${DateUtils.dayjsInMarketplaceTimezone(slot.start).format(this.translateService.instant('parameters.wantedAt.slots-format'))} - ${DateUtils.dayjsInMarketplaceTimezone(slot.end).format(this.translateService.instant('parameters.wantedAt.slots-format'))}`,
        slot: {
          start: new Date(DateUtils.dayjsInMarketplaceTimezone(slot.start).format()),
          end: new Date(DateUtils.dayjsInMarketplaceTimezone(slot.end).format()),
        },
      };
    });
    this.selectedTimeSlot = this.availableTimesSlots.find(
      timeSlot => timeSlot.slot.start.getHours() === this.selectedTimeSlot?.getHours(),
    )?.slot.start;

    if (!this.selectedTimeSlot) {
      this.selectedTimeSlot = this.availableTimesSlots[0].slot.start;
    }
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(KioskDistributionModePickerPageComponent, {
  'keyAliases': {},
});
